import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getQuizzes, createQuiz, updateQuiz, deleteQuiz, getLecturerQuiz, getAdminQuiz } from '../services/quizService';

const QuizContext = createContext();

export const QuizProvider = ({ children, isLecturer=false }) => {
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [topicId, setTopicId] = useState(null);

    // Fetch quizzes whenever topicId changes
    useEffect(() => {
        fetchQuizzes({ topic_id: topicId });
    }, [topicId]);

    const fetchQuizzes = async ({ topic_id = null }) => {
        try {
            setLoading(true);
            let data;
            if (isLecturer) {
                data = await getLecturerQuiz();
                console.log("Fetched lecturer quizzes:", data);
            } else {
                data = await getAdminQuiz();
                if (topicId) {
                    data = data.filter((data)=> data.topic == topic_id)
                }
                console.log("Fetched general quizzes:", data);
            }
            setQuizzes(data);
            return data;
        } catch (error) {
            console.error('Error fetching quizzes:', error);
            toast.error('Failed to fetch quizzes');
        } finally {
            setLoading(false);
        }
    };

    const addQuiz = async (quizData) => {
        try {
            const newQuiz = await createQuiz(quizData);
            setQuizzes([...quizzes, newQuiz]);
            return newQuiz;
        } catch (error) {
            console.error('Error adding quiz:', error);
            throw error;
        }
    };

    const editQuiz = async (quizId, quizData) => {
        try {
            const updatedQuiz = await updateQuiz(quizId, quizData);
            setQuizzes(quizzes.map(quiz => quiz.id === quizId ? updatedQuiz : quiz));
            return updatedQuiz;
        } catch (error) {
            console.error('Error editing quiz:', error);
            throw error;
        }
    };

    const removeQuiz = async (quizId) => {
        try {
            await deleteQuiz(quizId);
            setQuizzes(quizzes.filter(quiz => quiz.id !== quizId));
            toast.success('Quiz deleted successfully');
        } catch (error) {
            console.error('Error deleting quiz:', error);
            toast.error('Failed to delete quiz');
        }
    };

    return (
        <QuizContext.Provider value={{
            quizzes,
            loading,
            fetchQuizzes,
            addQuiz,
            editQuiz,
            removeQuiz,
            setTopicId,
        }}>
            {children}
        </QuizContext.Provider>
    );
};

export const useQuizzes = (topicId=null) => {
    const context = useContext(QuizContext);
    if (!context) {
        throw new Error('useQuizzes must be used within a QuizProvider');
    }

    // Automatically update topic ID when passed
    useEffect(() => {
        if (topicId !== null) {
            context.setTopicId(topicId);
        }
    }, [topicId]);

    return context;
};
